// Big Ideas Math Course 1 — California Edition (ISBN 978-1-60840-669-2, ©2015).
// Chapter and section structure mirrors the student textbook Grace uses at
// Foothills Middle School. Section numbers here match those in the book.

export type ChapterColor = 'mint' | 'sky' | 'sun' | 'coral' | 'violet';

export interface Section {
  id: string;
  title: string;
}

export interface Chapter {
  num: number;
  title: string;
  color: ChapterColor;
  sections: Section[];
}

export const chapters: Chapter[] = [
  {
    num: 1,
    title: 'Numerical Expressions and Factors',
    color: 'sky',
    sections: [
      { id: '1.1', title: 'Whole Number Operations' },
      { id: '1.2', title: 'Powers and Exponents' },
      { id: '1.3', title: 'Order of Operations' },
      { id: '1.4', title: 'Prime Factorization' },
      { id: '1.5', title: 'Greatest Common Factor' },
      { id: '1.6', title: 'Least Common Multiple' },
    ],
  },
  {
    num: 2,
    title: 'Fractions and Decimals',
    color: 'violet',
    sections: [
      { id: '2.1', title: 'Multiplying Fractions' },
      { id: '2.2', title: 'Dividing Fractions' },
      { id: '2.3', title: 'Dividing Mixed Numbers' },
      { id: '2.4', title: 'Adding and Subtracting Decimals' },
      { id: '2.5', title: 'Multiplying Decimals' },
      { id: '2.6', title: 'Dividing Decimals' },
    ],
  },
  {
    num: 3,
    title: 'Algebraic Expressions and Properties',
    color: 'sky',
    sections: [
      { id: '3.1', title: 'Algebraic Expressions' },
      { id: '3.2', title: 'Writing Expressions' },
      { id: '3.3', title: 'Properties of Addition and Multiplication' },
      { id: '3.4', title: 'The Distributive Property' },
    ],
  },
  {
    num: 4,
    title: 'Areas of Polygons',
    color: 'sun',
    sections: [
      { id: '4.1', title: 'Areas of Parallelograms' },
      { id: '4.2', title: 'Areas of Triangles' },
      { id: '4.3', title: 'Areas of Trapezoids' },
      { id: '4.4', title: 'Polygons in the Coordinate Plane' },
    ],
  },
  {
    num: 5,
    title: 'Ratios and Rates',
    color: 'violet',
    sections: [
      { id: '5.1', title: 'Ratios' },
      { id: '5.2', title: 'Ratio Tables' },
      { id: '5.3', title: 'Rates' },
      { id: '5.4', title: 'Comparing and Graphing Ratios' },
      { id: '5.5', title: 'Percents' },
      { id: '5.6', title: 'Solving Percent Problems' },
      { id: '5.7', title: 'Converting Measures' },
    ],
  },
  {
    num: 6,
    title: 'Integers and the Coordinate Plane',
    color: 'sky',
    sections: [
      { id: '6.1', title: 'Integers' },
      { id: '6.2', title: 'Comparing and Ordering Integers' },
      { id: '6.3', title: 'Fractions and Decimals on the Number Line' },
      { id: '6.4', title: 'Absolute Value' },
      { id: '6.5', title: 'The Coordinate Plane' },
    ],
  },
  {
    num: 7,
    title: 'Equations and Inequalities',
    color: 'coral',
    sections: [
      { id: '7.1', title: 'Writing Equations in One Variable' },
      { id: '7.2', title: 'Solving Equations Using Addition or Subtraction' },
      { id: '7.3', title: 'Solving Equations Using Multiplication or Division' },
      { id: '7.4', title: 'Writing Equations in Two Variables' },
      { id: '7.5', title: 'Writing and Graphing Inequalities' },
      { id: '7.6', title: 'Solving Inequalities Using Addition or Subtraction' },
      { id: '7.7', title: 'Solving Inequalities Using Multiplication or Division' },
    ],
  },
  {
    num: 8,
    title: 'Surface Area and Volume',
    color: 'violet',
    sections: [
      { id: '8.1', title: 'Three-Dimensional Figures' },
      { id: '8.2', title: 'Surface Areas of Prisms' },
      { id: '8.3', title: 'Surface Areas of Pyramids' },
      { id: '8.4', title: 'Volumes of Rectangular Prisms' },
    ],
  },
  {
    num: 9,
    title: 'Statistical Measures',
    color: 'sun',
    sections: [
      { id: '9.1', title: 'Introduction to Statistics' },
      { id: '9.2', title: 'Mean' },
      { id: '9.3', title: 'Measures of Center' },
      { id: '9.4', title: 'Measures of Variation' },
      { id: '9.5', title: 'Mean Absolute Deviation' },
    ],
  },
  {
    num: 10,
    title: 'Data Displays',
    color: 'coral',
    sections: [
      { id: '10.1', title: 'Stem-and-Leaf Plots' },
      { id: '10.2', title: 'Histograms' },
      { id: '10.3', title: 'Shapes of Distributions' },
      { id: '10.4', title: 'Box-and-Whisker Plots' },
    ],
  },
];

export function findSection(chapterId: string | null): { chapter: Chapter; section: Section } | null {
  if (!chapterId) return null;
  for (const chapter of chapters) {
    const section = chapter.sections.find((s) => s.id === chapterId);
    if (section) return { chapter, section };
  }
  return null;
}
